// This file is auto-generated by @hey-api/openapi-ts

import { client } from './Client.gen';
import type { EshopOrderControllerCancelData } from './EshopOrderControllerCancelData.gen';
import type { EshopOrderControllerCancelErrors } from './EshopOrderControllerCancelErrors.gen';
import type { EshopOrderControllerCancelResponses } from './EshopOrderControllerCancelResponses.gen';
import type { EshopOrderControllerCreateOrderData } from './EshopOrderControllerCreateOrderData.gen';
import type { EshopOrderControllerCreateOrderErrors } from './EshopOrderControllerCreateOrderErrors.gen';
import type { EshopOrderControllerCreateOrderResponses } from './EshopOrderControllerCreateOrderResponses.gen';
import type { EshopOrderControllerGetAllData } from './EshopOrderControllerGetAllData.gen';
import type { EshopOrderControllerGetAllErrors } from './EshopOrderControllerGetAllErrors.gen';
import type { EshopOrderControllerGetAllResponses } from './EshopOrderControllerGetAllResponses.gen';
import type { EshopOrderControllerGetOrderDetailData } from './EshopOrderControllerGetOrderDetailData.gen';
import type { EshopOrderControllerGetOrderDetailErrors } from './EshopOrderControllerGetOrderDetailErrors.gen';
import type { EshopOrderControllerGetOrderDetailResponses } from './EshopOrderControllerGetOrderDetailResponses.gen';
import type { EshopOrderControllerInitPaymentData } from './EshopOrderControllerInitPaymentData.gen';
import type { EshopOrderControllerInitPaymentErrors } from './EshopOrderControllerInitPaymentErrors.gen';
import type { EshopOrderControllerInitPaymentResponses } from './EshopOrderControllerInitPaymentResponses.gen';
import type { EshopOrderControllerPayAgainData } from './EshopOrderControllerPayAgainData.gen';
import type { EshopOrderControllerPayAgainErrors } from './EshopOrderControllerPayAgainErrors.gen';
import type { EshopOrderControllerPayAgainResponses } from './EshopOrderControllerPayAgainResponses.gen';
import type { EshopOrderControllerSendOrderPaidEmailData } from './EshopOrderControllerSendOrderPaidEmailData.gen';
import type { EshopOrderControllerSendOrderPaidEmailErrors } from './EshopOrderControllerSendOrderPaidEmailErrors.gen';
import type { EshopOrderControllerSendOrderPaidEmailResponses } from './EshopOrderControllerSendOrderPaidEmailResponses.gen';
import type { EshopOrderControllerVerifyIfCardApplicationCanBeCreatedData } from './EshopOrderControllerVerifyIfCardApplicationCanBeCreatedData.gen';
import type { EshopOrderControllerVerifyIfCardApplicationCanBeCreatedErrors } from './EshopOrderControllerVerifyIfCardApplicationCanBeCreatedErrors.gen';
import type { EshopOrderControllerVerifyIfCardApplicationCanBeCreatedResponses } from './EshopOrderControllerVerifyIfCardApplicationCanBeCreatedResponses.gen';
import type { Options } from './Options.gen';

export class EshopOrderController {
    /**
     * Get eshop orders.
     */
    public static getAll<ThrowOnError extends boolean = false>(options?: Options<EshopOrderControllerGetAllData, ThrowOnError>) {
        return (options?.client ?? client).get<EshopOrderControllerGetAllResponses, EshopOrderControllerGetAllErrors, ThrowOnError>({
            security: [{ scheme: 'bearer', type: 'http' }],
            url: '/api/eshop/orders',
            ...options
        });
    }
    
    /**
     * Create an order from eshop shopping cart.
     *
     * ### Error names for HTTP Status 400 BadRequest:<br>
     * + **VALIDATION_ERROR** - Invalid body, check 'errors' property for more info.<br>
     * + **CUSTOMER_ACCOUNT_NOT_VERIFIED** - Customer account is not verified.<br>
     * + **SHOPPING_CART_IS_EMPTY** - Cannot create an order. Shopping cart is empty.<br>
     * + **INVALID_PAYMENT_TYPE** - Payment type does not exist or is not allowed to use.<br>
     * + **INVALID_FEE_TRANSACTION_STATE** - Fee transaction is already assigned to order or is paid.
     */
    public static createOrder<ThrowOnError extends boolean = false>(options: Options<EshopOrderControllerCreateOrderData, ThrowOnError>) {
        return (options.client ?? client).post<EshopOrderControllerCreateOrderResponses, EshopOrderControllerCreateOrderErrors, ThrowOnError>({
            security: [{ scheme: 'bearer', type: 'http' }],
            url: '/api/eshop/orders',
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...options.headers
            }
        });
    }
    
    /**
     * Get eshop order.
     */
    public static getOrderDetail<ThrowOnError extends boolean = false>(options: Options<EshopOrderControllerGetOrderDetailData, ThrowOnError>) {
        return (options.client ?? client).get<EshopOrderControllerGetOrderDetailResponses, EshopOrderControllerGetOrderDetailErrors, ThrowOnError>({
            security: [{ scheme: 'bearer', type: 'http' }],
            url: '/api/eshop/orders/{id}',
            ...options
        });
    }
    
    /**
     * Init a payment.
     *
     * ### Error names for HTTP Status 400 BadRequest:<br>
     * + **VALIDATION_ERROR** - Invalid body, check 'errors' property for more info.<br>
     * + **CUSTOMER_ACCOUNT_NOT_VERIFIED** - Customer account is not verified.<br>
     * + **CANNOT_INIT_PAYMENT** - CSOB Gateway cannot be initialized.
     */
    public static initPayment<ThrowOnError extends boolean = false>(options: Options<EshopOrderControllerInitPaymentData, ThrowOnError>) {
        return (options.client ?? client).post<EshopOrderControllerInitPaymentResponses, EshopOrderControllerInitPaymentErrors, ThrowOnError>({
            security: [{ scheme: 'bearer', type: 'http' }],
            url: '/api/eshop/orders/{id}/init-payment',
            ...options
        });
    }
    
    /**
     * Init a payment.
     *
     * ### Error names for HTTP Status 400 BadRequest:<br>
     * + **VALIDATION_ERROR** - Invalid body, check 'errors' property for more info.<br>
     * + **CUSTOMER_ACCOUNT_NOT_VERIFIED** - Customer account is not verified.<br>
     * + **CANNOT_PAY_AGAIN** - Cannot pay this order again.
     */
    public static payAgain<ThrowOnError extends boolean = false>(options: Options<EshopOrderControllerPayAgainData, ThrowOnError>) {
        return (options.client ?? client).post<EshopOrderControllerPayAgainResponses, EshopOrderControllerPayAgainErrors, ThrowOnError>({
            security: [{ scheme: 'bearer', type: 'http' }],
            url: '/api/eshop/orders/{id}/pay-again',
            ...options
        });
    }
    
    /**
     * Cancel order
     *
     * ### Error names for HTTP Status 400 BadRequest:<br>
     * + **VALIDATION_ERROR** - Invalid body, check 'errors' property for more info.<br>
     * + **CUSTOMER_ACCOUNT_NOT_VERIFIED** - Customer account is not verified.<br>
     * + **INVALID_ORDER_STATE** - Eshop order state is invalid or does not exist.
     */
    public static cancel<ThrowOnError extends boolean = false>(options: Options<EshopOrderControllerCancelData, ThrowOnError>) {
        return (options.client ?? client).put<EshopOrderControllerCancelResponses, EshopOrderControllerCancelErrors, ThrowOnError>({
            security: [{ scheme: 'bearer', type: 'http' }],
            url: '/api/eshop/orders/{id}/cancel',
            ...options
        });
    }
    
    /**
     * Send payment email
     *
     * ### Error names for HTTP Status 400 BadRequest:<br>
     * + **VALIDATION_ERROR** - Invalid body, check 'errors' property for more info.
     */
    public static sendOrderPaidEmail<ThrowOnError extends boolean = false>(options: Options<EshopOrderControllerSendOrderPaidEmailData, ThrowOnError>) {
        return (options.client ?? client).put<EshopOrderControllerSendOrderPaidEmailResponses, EshopOrderControllerSendOrderPaidEmailErrors, ThrowOnError>({
            security: [{ scheme: 'bearer', type: 'http' }],
            url: '/api/eshop/orders/{id}/send-payment-email',
            ...options
        });
    }
    
    /**
     * Check if order is for card application and if is in processing state.
     */
    public static verifyIfCardApplicationCanBeCreated<ThrowOnError extends boolean = false>(options: Options<EshopOrderControllerVerifyIfCardApplicationCanBeCreatedData, ThrowOnError>) {
        return (options.client ?? client).get<EshopOrderControllerVerifyIfCardApplicationCanBeCreatedResponses, EshopOrderControllerVerifyIfCardApplicationCanBeCreatedErrors, ThrowOnError>({
            security: [{ scheme: 'bearer', type: 'http' }],
            url: '/api/eshop/orders-application-state-check',
            ...options
        });
    }
}
